import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CircleNotch, TrendDown, ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currencySymbols = { USD: '$', GBP: '\u00A3', AUD: 'A$' };

export default function PriceDropsPage() {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/price-drops`)
      .then(({ data }) => setDrops(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><CircleNotch size={48} className="animate-spin text-[#FF3B30]" /></div>;

  return (
    <div className="min-h-screen bg-white" data-testid="price-drops-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center gap-3 mb-2">
          <TrendDown size={28} className="text-[#10B981]" />
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase" data-testid="price-drops-title">Price Drops</h1>
        </div>
        <p className="text-[#52525B] mb-10">Recent price reductions across stores.</p>

        {drops.length > 0 ? (
          <div className="space-y-4">
            {drops.map((drop, i) => (
              <motion.div key={`${drop.shoe_id}-${drop.store_id}-${i}`} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Link to={`/shoe/${drop.shoe_id}`}
                  className="flex items-center gap-4 sm:gap-6 border border-[#E4E4E7] p-4 hover:border-[#0A0A0A] transition-colors group"
                  data-testid={`drop-card-${drop.shoe_id}`}>
                  {drop.image_url && (
                    <img src={drop.image_url} alt={drop.model} className="w-16 h-16 sm:w-20 sm:h-20 object-cover shrink-0 border border-[#E4E4E7]"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">{drop.brand}</p>
                    <h3 className="font-heading font-bold text-base truncate">{drop.model}</h3>
                    <p className="text-xs text-[#52525B] mt-0.5">{drop.store_id?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm text-[#A1A1AA] line-through">{currencySymbols[drop.currency]}{drop.old_price?.toFixed(2)}</p>
                    <p className="font-bold text-lg">{currencySymbols[drop.currency]}{drop.new_price?.toFixed(2)}</p>
                    <span className="inline-block bg-[#10B981] text-white text-[10px] font-bold px-2 py-0.5 mt-1">
                      -{drop.drop_pct}%
                    </span>
                  </div>
                  <ArrowRight size={18} className="text-[#A1A1AA] group-hover:text-[#FF3B30] transition-colors shrink-0 hidden sm:block" />
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 border border-[#E4E4E7]">
            <TrendDown size={48} className="text-[#E4E4E7] mx-auto mb-4" />
            <p className="text-[#52525B] text-lg">No price drops recorded yet.</p>
            <p className="text-sm text-[#A1A1AA] mt-1">Check back soon — we monitor prices across 125+ stores daily.</p>
          </div>
        )}
      </div>
    </div>
  );
}
